/**
 * AI Generation API Client with React Query Hooks
 *
 * Provides mutation hooks for AI-assisted word metadata generation:
 * - Example sentences
 * - Translations (word and sentence)
 * - IPA transcription
 * - Pronunciation audio (TTS)
 * - Vocabulary images
 */

import { useMutation } from '@tanstack/react-query';
import { apiPost } from 'services/api';
import logger from 'utils/logger';
import type {
  GenerateExampleSentenceRequest,
  GenerateExampleSentenceResponse,
  GenerateTranslationsRequest,
  GenerateTranslationsResponse,
  GenerateExampleTranslationsRequest,
  GenerateExampleTranslationsResponse,
  GenerateIPARequest,
  GenerateIPAResponse,
  GeneratePronunciationAudioRequest,
  GeneratePronunciationAudioResponse,
  GenerateAllPronunciationsRequest,
  GenerateAllPronunciationsResponse,
  AllPronunciationsResult,
  GenerateImageRequest,
  GenerateImageResponse,
  GenerateWordMetadataRequest,
  GenerateWordMetadataResponse,
  WordMetadataResult,
} from '../types/word.types';

// ============================================================================
// API Functions
// ============================================================================

const generateExampleSentence = async (data: GenerateExampleSentenceRequest): Promise<string> => {
  const response = await apiPost<GenerateExampleSentenceResponse>(
    '/api/admin/words/generate-example',
    data
  );
  return response.example_sentence;
};

const generateTranslations = async (
  data: GenerateTranslationsRequest
): Promise<Record<string, string>> => {
  const response = await apiPost<GenerateTranslationsResponse>(
    '/api/admin/words/generate-translations',
    data
  );
  return response.translations;
};

const generateExampleTranslations = async (
  data: GenerateExampleTranslationsRequest
): Promise<Record<string, string>> => {
  const response = await apiPost<GenerateExampleTranslationsResponse>(
    '/api/admin/words/generate-example-translations',
    data
  );
  return response.translations;
};

const generateIPA = async (data: GenerateIPARequest): Promise<string> => {
  const response = await apiPost<GenerateIPAResponse>('/api/admin/words/generate-ipa', data);
  return response.ipa;
};

interface PronunciationAudioResult {
  audioUrl: string;
  durationSeconds: number;
}

const generatePronunciationAudio = async (
  data: GeneratePronunciationAudioRequest
): Promise<PronunciationAudioResult> => {
  const response = await apiPost<GeneratePronunciationAudioResponse>(
    '/api/admin/words/generate-pronunciation-audio',
    data
  );
  return {
    audioUrl: response.audio_url,
    durationSeconds: response.duration_seconds,
  };
};

const IMAGE_GENERATION_TIMEOUT_MS = 60000; // 60 seconds for image generation

const generateImage = async (data: GenerateImageRequest): Promise<string> => {
  const response = await apiPost<GenerateImageResponse>(
    '/api/admin/words/generate-image',
    data,
    IMAGE_GENERATION_TIMEOUT_MS
  );
  return response.image_url;
};

/**
 * Generate pronunciation (IPA + audio) for all configured locales of a language.
 */
const generateAllPronunciations = async (
  data: GenerateAllPronunciationsRequest
): Promise<AllPronunciationsResult> => {
  const response = await apiPost<GenerateAllPronunciationsResponse>(
    '/api/admin/words/generate-all-pronunciations',
    data
  );
  return {
    pronunciations: response.pronunciations.map((p) => ({
      locale: p.locale,
      localeName: p.locale_name,
      ipa: p.ipa,
      audioUrl: p.audio_url,
      durationSeconds: p.duration_seconds,
    })),
    totalGenerated: response.total_generated,
  };
};

const WORD_METADATA_TIMEOUT_MS = 60000; // 60 seconds for LLM generation

/**
 * Generate all text metadata for a word in a single LLM request.
 * Returns example sentence, word translations, and example translations.
 */
const generateWordMetadata = async (
  data: GenerateWordMetadataRequest
): Promise<WordMetadataResult> => {
  const response = await apiPost<GenerateWordMetadataResponse>(
    '/api/admin/words/generate-word-metadata',
    data,
    WORD_METADATA_TIMEOUT_MS
  );
  return {
    exampleSentence: response.example_sentence,
    wordTranslations: response.word_translations,
    exampleTranslations: response.example_translations,
  };
};

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Generate an example sentence for a word using LLM
 */
export const useGenerateExampleSentence = () => {
  return useMutation({
    mutationFn: (data: GenerateExampleSentenceRequest) => generateExampleSentence(data),
    onError: (error) => {
      logger.error('Error generating example sentence:', error);
    },
  });
};

/**
 * Generate translations of a word to multiple target languages
 */
export const useGenerateTranslations = () => {
  return useMutation({
    mutationFn: (data: GenerateTranslationsRequest) => generateTranslations(data),
    onError: (error) => {
      logger.error('Error generating translations:', error);
    },
  });
};

/**
 * Generate translations of an example sentence to multiple target languages
 */
export const useGenerateExampleTranslations = () => {
  return useMutation({
    mutationFn: (data: GenerateExampleTranslationsRequest) => generateExampleTranslations(data),
    onError: (error) => {
      logger.error('Error generating example translations:', error);
    },
  });
};

/**
 * Generate IPA transcription for a word in a specific locale
 */
export const useGenerateIPA = () => {
  return useMutation({
    mutationFn: (data: GenerateIPARequest) => generateIPA(data),
    onError: (error) => {
      logger.error('Error generating IPA:', error);
    },
  });
};

/**
 * Generate pronunciation audio for a word using TTS
 */
export const useGeneratePronunciationAudio = () => {
  return useMutation({
    mutationFn: (data: GeneratePronunciationAudioRequest) => generatePronunciationAudio(data),
    onError: (error) => {
      logger.error('Error generating pronunciation audio:', error);
    },
  });
};

/**
 * Generate a vocabulary illustration image using OpenAI Image API
 */
export const useGenerateImage = () => {
  return useMutation({
    mutationFn: (data: GenerateImageRequest) => generateImage(data),
    onError: (error) => {
      logger.error('Error generating image:', error);
    },
  });
};

/**
 * Generate all text metadata (example sentence, translations, example translations)
 * in a single LLM request for efficiency.
 */
export const useGenerateWordMetadata = () => {
  return useMutation({
    mutationFn: (data: GenerateWordMetadataRequest) => generateWordMetadata(data),
    onError: (error) => {
      logger.error('Error generating word metadata:', error);
    },
  });
};

/**
 * Generate pronunciations (IPA + audio) for all configured locales of a language.
 * Only generates for locales that have pronunciation_voice_id configured.
 */
export const useGenerateAllPronunciations = () => {
  return useMutation({
    mutationFn: (data: GenerateAllPronunciationsRequest) => generateAllPronunciations(data),
    onError: (error) => {
      logger.error('Error generating all pronunciations:', error);
    },
  });
};
